/**
 * In-memory pending-offer registry for INCOMING file-transfer offers. Web's
 * mirror of `apps/extension/src/background/ftOfferStore.ts` — same shape and
 * semantics (dedup by transferId, 1h expiry timer), scoped to the page's
 * module state instead of the SW's. Wiped on page reload — acceptable per
 * the same "in-memory-by-design" rule the extension's SW-scoped store
 * documents: a lost offer just means the sender's `ACCEPT_TIMEOUT_MS`
 * no-answer path fires, a harmless retry, not silent data corruption.
 *
 * Security: manifest contents (filename, mime, etc.) are E2E-only per
 * `TransferManifest`'s doc — NEVER logged (security.md).
 */

import type { TransferManifest } from "@reflecto/core";

const OFFER_EXPIRY_MS = 60 * 60 * 1000; // 1 hour — matches FT_OFFER_TTL_MS (§7.1)

export interface PendingOffer {
  manifest: TransferManifest;
  /** Unix ms when the offer was registered (arrival time, not `manifest.createdAt`). */
  receivedAt: number;
}

interface OfferEntry extends PendingOffer {
  timer: ReturnType<typeof setTimeout>;
}

const offers = new Map<string, OfferEntry>();

/**
 * Register a pending offer, keyed by `manifest.transferId`. Dedupes: an
 * existing pending entry for the same transferId is left unchanged (first
 * offer wins; its expiry timer keeps running).
 */
export function addOffer(manifest: TransferManifest): PendingOffer {
  const existing = offers.get(manifest.transferId);
  if (existing) return existing;
  const timer = setTimeout(() => {
    offers.delete(manifest.transferId);
  }, OFFER_EXPIRY_MS);
  const entry: OfferEntry = { manifest, receivedAt: Date.now(), timer };
  offers.set(manifest.transferId, entry);
  return entry;
}

/** Look up a pending offer by transferId. Returns undefined if absent or already expired/removed. */
export function getOffer(transferId: string): PendingOffer | undefined {
  return offers.get(transferId);
}

/** Snapshot every currently-pending offer, keyed by transferId. */
export function listOffers(): Array<{ transferId: string } & PendingOffer> {
  return Array.from(offers.entries()).map(([transferId, entry]) => ({
    transferId,
    manifest: entry.manifest,
    receivedAt: entry.receivedAt,
  }));
}

/** Remove a pending offer and cancel its expiry timer. No-op if not found. */
export function removeOffer(transferId: string): void {
  const entry = offers.get(transferId);
  if (entry) {
    clearTimeout(entry.timer);
    offers.delete(transferId);
  }
}

/** Clear all pending offers and cancel their timers (unpair, or test teardown). */
export function clearOffers(): void {
  for (const entry of offers.values()) {
    clearTimeout(entry.timer);
  }
  offers.clear();
}
